import type {
  QuestionnaireDraft,
  QuestionnaireValidationIssue,
} from "@digital-twins/shared-types";

import { getValidationTarget } from "../draft/createLocalDraft";

/**
 * The blocking issues the author is shown: the banner and Review's tooltip
 * read this one list, so they never disagree about why Review is locked.
 *
 * Two issues are the questionnaire not existing yet, not something to fix:
 * - before any question exists, the count (the chat or Build manually adds
 *   the first one);
 * - a shell still collecting its brief is missing its problem statement by
 *   design: the pre-generation shell the builder has no workspace draft for
 *   (`draft` absent), or a `collecting_problem` draft with no questions and
 *   a blank problem statement.
 *
 * Everything else stays, including the same artifact error at any other
 * path or status.
 */
export function shownAuthoringIssues(
  issues: ReadonlyArray<QuestionnaireValidationIssue> | undefined,
  draft:
    | Pick<QuestionnaireDraft, "researchQuestions" | "status" | "problemStatement">
    | null
    | undefined,
): Array<QuestionnaireValidationIssue> {
  const questionCount = draft?.researchQuestions.length ?? 0;
  const collectingBrief =
    !draft ||
    (questionCount === 0 &&
      draft.status === "collecting_problem" &&
      typeof draft.problemStatement === "string" &&
      draft.problemStatement.trim().length === 0);
  return (issues ?? []).filter(
    (issue) =>
      !(
        questionCount === 0 && issue.code === "research_questions_too_few"
      ) &&
      !(
        collectingBrief &&
        issue.code === "accepted_artifact_contract_invalid" &&
        issue.path === "problemStatement"
      ),
  );
}

/**
 * Key blocking issues by question id against the persisted draft the
 * readiness verdict describes, never the live editable copy.
 *
 * A bare `researchQuestions` path (no index) is unscoped — it belongs on
 * the banner, not on Q1.
 *
 * An issue about one tested asset (its typed `assetId`, else its indexed
 * `testedAssets.assets[N]` path) is also keyed by that asset's id, for its
 * card; it stays unscoped too, as the banner's assets line (QA4 FE3: the
 * server's verdict is what the asset cards show, not a client rule).
 */
export function indexBlockingIssuesByQuestionId(
  issues: Array<QuestionnaireValidationIssue> | undefined,
  persistedDraft: QuestionnaireDraft | null,
): {
  byQuestionId: Map<string, Array<QuestionnaireValidationIssue>>;
  byAssetId: Map<string, Array<QuestionnaireValidationIssue>>;
  unscoped: Array<QuestionnaireValidationIssue>;
} {
  const byQuestionId = new Map<string, Array<QuestionnaireValidationIssue>>();
  const byAssetId = new Map<string, Array<QuestionnaireValidationIssue>>();
  const unscoped: Array<QuestionnaireValidationIssue> = [];
  if (!issues || issues.length === 0 || !persistedDraft) {
    return { byQuestionId, byAssetId, unscoped };
  }

  for (const issue of issues) {
    const target = getValidationTarget(persistedDraft, issue.path ?? "");
    const questionId = target.questionId;
    if (!questionId) {
      unscoped.push(issue);
      const assetId = issue.assetId ?? target.assetId;
      if (assetId)
        byAssetId.set(assetId, [...(byAssetId.get(assetId) ?? []), issue]);
      continue;
    }
    const existing = byQuestionId.get(questionId);
    if (existing) {
      existing.push(issue);
    } else {
      byQuestionId.set(questionId, [issue]);
    }
  }

  return { byQuestionId, byAssetId, unscoped };
}
